import { Box, SxProps, Theme, Typography, Breadcrumbs, Link, IconButton, alpha, useTheme } from '@mui/material';
import React from 'react';
import { CurrencyType, TextType } from '../TextBox/WSTextBox';
import { ArrowBack as ArrowBackIcon, Home as HomeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Define an interface for your component props
interface ViewProps {
    labelStyle?: React.CSSProperties;
    header?: string;
    subHeader?: string;
    backGroundColor?: string;
    sx?: SxProps<Theme>;
    columns?: IControls[];
    showBackButton?: boolean;
    onBackClick?: () => void;
    breadcrumbs?: Array<{ label: string; path?: string }>;
    actions?: React.ReactNode;
    variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    gradient?: boolean;
}

export interface IControls {
    label: string;
    control: any
    type?: 'TextField' | 'Dropdown' | 'Date' | 'Phone' | 'Email' | 'Radio' | 'CheckBox' | 'FileUpload'
    style?: React.CSSProperties;
    onChange?: (evt: any) => void
    deFaultValue?: any
    name: string
    sm?: number,
    xs?: number,
    rules: any,
    max?: number,
    min?: number,
    currency?: CurrencyType
    options?: Array<any>,
    source?: string
    textType: TextType
}

export const WSHeaderCard: React.FC<ViewProps> = React.memo(({ 
    sx, 
    header, 
    subHeader,
    backGroundColor, 
    showBackButton = false,
    onBackClick,
    breadcrumbs,
    actions,
    variant = 'h5',
    gradient = false
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const handleBack = () => {
        if (onBackClick) {
            onBackClick();
        } else {
            navigate(-1);
        }
    };

    const getHeaderStyles = () => {
        if (gradient) {
            return {
                background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            };
        }
        return {
            backgroundColor: backGroundColor || alpha(theme.palette.secondary.main, 0.05),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        };
    };

    return (
        <Box sx={{ mb: 4, ...sx }}>
            {/* Breadcrumbs */}
            {breadcrumbs && breadcrumbs.length > 0 && (
                <Breadcrumbs 
                    sx={{ mb: 2 }}
                    separator="›"
                    aria-label="breadcrumb"
                >
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/')}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'text.secondary',
                            textDecoration: 'none',
                            '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                            }
                        }}
                    >
                        <HomeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                        Home
                    </Link>
                    {breadcrumbs.map((item, index) => {
                        const isLast = index === breadcrumbs.length - 1;
                        return isLast ? (
                            <Typography
                                key={index}
                                variant="body2"
                                sx={{ color: 'text.primary', fontWeight: 500 }}
                            >
                                {item.label}
                            </Typography>
                        ) : (
                            <Link
                                key={index}
                                component="button"
                                variant="body2"
                                onClick={() => item.path && navigate(item.path)}
                                sx={{
                                    color: 'text.secondary',
                                    textDecoration: 'none',
                                    '&:hover': {
                                        color: 'primary.main',
                                        textDecoration: 'underline',
                                    }
                                }}
                            >
                                {item.label}
                            </Link>
                        );
                    })}
                </Breadcrumbs>
            )}

            {/* Header Card */}
            <Box
                sx={{
                    ...getHeaderStyles(),
                    padding: '16px 24px',
                    borderRadius: 2,
                    boxShadow: gradient ? `0 4px 15px ${alpha(theme.palette.secondary.main, 0.2)}` : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Back Button */}
                    {showBackButton && (
                        <IconButton
                            onClick={handleBack}
                            sx={{
                                backgroundColor: alpha(theme.palette.common.white, 0.1),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.common.white, 0.2),
                                },
                                ...(gradient && {
                                    color: 'white',
                                })
                            }}
                            size="small"
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    )}

                    {/* Header Text */}
                    <Box>
                        {header && (
                            <Typography
                                variant={variant}
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '-0.5px',
                                    color: gradient ? 'white' : 'text.primary',
                                    lineHeight: 1.3,
                                }}
                            >
                                {header}
                            </Typography>
                        )}
                        {subHeader && (
                            <Typography
                                variant="body2"
                                sx={{
                                    color: gradient ? alpha('#fff', 0.9) : 'text.secondary',
                                    mt: 0.5,
                                }}
                            >
                                {subHeader}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Actions */}
                {actions && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {actions}
                    </Box>
                )}
            </Box>
        </Box>
    );
});

WSHeaderCard.displayName = 'WSHeaderCard';